<template>
    <CRow>
        <CCol col="12" lg="12">
            <CAlert :color="messageColor" closeButton :show.sync="dismissCountDown" fade>
                {{ alertMessage }}
            </CAlert>

            <CCard>
                <CCardHeader>
                    <strong>{{ $lang.scholarship.crud.create }}</strong>
                </CCardHeader>

                <CForm novalidate @submit.prevent="onSubmit" enctype='multipart/form-data'>
                    <CCardBody>
                        <CRow>
                            <CCol md="6">
                                <div class="form-group" :class="{ 'form-group--error': $v.scholarship.title.$error }">
                                    <label class="form__label">Title <required_span /></label>
                                    <input type="text" class="form-control" v-model="scholarship.title" />
                                    <small class="error" v-if="$v.scholarship.title.$error">{{
                                        $lang.scholarship.validation.required.title
                                    }}</small>
                                </div>
                            </CCol>
                            <CCol md="6">
                                <div class="form-group" :class="{ 'form-group--error': $v.scholarship.type.$error }">
                                    <label class="form__label">Type </label>
                                    <v-select :options="scholarshipTypeOption" v-model="scholarship.type" />
                                    <small class="error" v-if="$v.scholarship.type.$error">{{
                                        $lang.scholarship.validation.required.type }}</small>
                                </div>
                            </CCol>
                        </CRow>

                        <CRow>
                            <CCol md="12">
                                <div class="form-group">
                                    <label class="form__label">Tags</label>
                                    <v-select
                                        v-model="scholarship.tags"
                                        :options=scholarshipTagsOption
                                        multiple
                                        :taggable="true"
                                        :close-on-select="false"
                                        placeholder="Type and press Enter to add tags"
                                    />
                                    <!-- <input type="text" class="form-control" v-model="scholarship.tags" /> -->
                                </div>
                            </CCol>
                        </CRow>
                        <CRow>
                            <CCol md="12">
                                <div class="form-group" :class="{ 'form-group--error': $v.scholarship.summary.$error }">
                                    <label class="form__label">Short Summary <required_span /></label>
                                    <textarea class="form-control" v-model="scholarship.summary" rows="3"></textarea>
                                    <small class="error" v-if="$v.scholarship.summary.$error">{{
                                        $lang.scholarship.validation.required.summary
                                    }}</small>
                                </div>
                            </CCol>
                        </CRow>
                        <CRow>
                            <CCol md="12">
                                <div class="form-group">
                                    <label class="form__label">Benefits</label>
                                    <textarea class="form-control" v-model="scholarship.benefits" rows="3"></textarea>
                                </div>
                            </CCol>
                        </CRow>
                        <hr />
                        <h5>Application Process</h5>
                        <CRow class="margin-bottom-20">
                            <CCol md="6">
                                <div class="form-group" :class="{ 'form-group--error': $v.scholarship.application_mode.$error }">
                                    <label class="form__label">Application Mode</label>
                                    <v-select :options="applicationModeOption" v-model="scholarship.application_mode" @input="onApplicationModeChange"/>
                                    <small class="error" v-if="$v.scholarship.application_mode.$error">{{
                                        $lang.scholarship.validation.required.application_mode
                                    }}</small>
                                </div>
                            </CCol>
                            <CCol v-if="scholarship.application_mode_value === 1 || scholarship.application_mode_value === 3" md="6">
                                <div class="form-group">
                                    <label class="form__label">Application URL</label>
                                    <input type="url" class="form-control" v-model="scholarship.application_url" />
                                </div>
                            </CCol>
                            <CCol v-if="scholarship.application_mode_value === 2 || scholarship.application_mode_value === 3" md="6">
                                <label class="form__label">Application Locations</label>
                                <div v-for="(location, index) in scholarship.application_locations" :key="index" class="mb-2">
                                    <div class="d-flex align-items-center">
                                        <input type="text" class="form-control"
                                            v-model="scholarship.application_locations[index]" />
                                        <CButton v-if="scholarship.application_locations.length > 1"
                                            @click="removeLocation(index)" color="danger" size="sm" class="ml-2">
                                            Remove
                                        </CButton>
                                    </div>
                                </div>
                                <CButton @click="addLocation" color="primary" size="sm">Add Location</CButton>
                            </CCol>
                        </CRow>
                        <CRow>
                            <CCol md="6">
                                <div class="form-group">
                                    <label class="form__label">Provider Type</label>
                                    <v-select :options="providerTypeOption" v-model="scholarship.provider_type" multiple />
                                </div>
                            </CCol>

                            <CCol md="6">
                                <div class="form-group">
                                    <label class="form__label">Required Documents</label>
                                    <v-select :options="requiredDocumentsOption" v-model="scholarship.required_document" multiple />
                                </div>
                            </CCol>
                        </CRow>

                        <CRow>
                            <CCol md="12">
                                <div class="form-group">
                                    <label class="form__label">How to Apply</label>
                                    <textarea class="form-control" v-model="scholarship.how_to_apply" rows="5"></textarea>
                                </div>
                            </CCol>
                        </CRow>
                        <hr />
                        <h5>Application Timeline</h5>
                        <CRow>
                            <CCol md="6">
                                <div class="form-group">
                                    <label class="form__label">Academic Year</label>
                                    <input type="text" class="form-control" v-model="scholarship.academic_year" />
                                </div>
                            </CCol>
                            <CCol md="6">
                                <div class="form-group">
                                    <label class="form__label">Result Date</label>
                                    <input type="date" class="form-control" v-model="scholarship.result_date" />
                                </div>
                            </CCol>
                        </CRow>
                        <CRow>
                            <CCol md="6">
                                <div class="form-group"
                                    :class="{ 'form-group--error': $v.scholarship.startDate.$error }">
                                    <label class="form__label">Start Date <required_span /></label>
                                    <input type="date" class="form-control" v-model="scholarship.startDate" />
                                    <small class="error" v-if="$v.scholarship.startDate.$error">{{
                                        $lang.scholarship.validation.required.startDate
                                    }}</small>
                                </div>
                            </CCol>

                            <CCol md="6">
                                <div class="form-group"
                                    :class="{ 'form-group--error': $v.scholarship.endDate.$error }">
                                    <label class="form__label">End Date <required_span /></label>
                                    <input type="date" class="form-control" v-model="scholarship.endDate" />
                                    <small class="error" v-if="$v.scholarship.endDate.$error">{{
                                        $lang.scholarship.validation.required.endDate }}</small>
                                </div>
                            </CCol>
                        </CRow>
                        <hr />
                        <h5>Eligibility Criteria</h5>
                        <CRow>
                            <CCol md="4">
                                <div class="form-group">
                                    <label class="form__label">Programmes </label>
                                    <v-select :options="collegeProgrammesOption" v-model="scholarship.college_programme" @input="onCollegeProgrammesChange" />
                                </div>
                            </CCol>

                            <CCol md="4">
                                <div class="form-group">
                                    <label class="form__label">Stream </label>
                                    <v-select :options="collegeStreamOption" v-model="scholarship.college_stream"  @input="onCollegeStreamChange" />
                                </div>
                            </CCol>

                            <CCol md="4">
                                <div class="form-group">
                                    <label class="form__label">Disciplines </label>
                                    <v-select :options="collegeDisciplinesOption" v-model="scholarship.college_disciplines" multiple />
                                </div>
                            </CCol>
                        </CRow>
                        <CRow>
                             <CCol md="6">
                                <div class="form-group">
                                    <label class="form__label">Marks Type</label>
                                    <v-select :options="marksTypeOption" v-model="scholarship.marks_type" @input="onMarksTypeChange"/>
                                </div>
                            </CCol>
                            <CCol md="6" v-if="scholarship.marks_type.id == 1">
                                <div class="form-group">
                                    <label class="form__label">Min Percentage</label>
                                    <input type="number" class="form-control" v-model="scholarship.min_percentage" />
                                </div>
                            </CCol>

                            <CCol md="3" v-if="scholarship.marks_type.id == 2">
                                <div class="form-group">
                                    <label class="form__label">GPA Score</label>
                                    <input type="number" step="0.01" class="form-control" v-model="scholarship.gpa_score" />
                                </div>
                            </CCol>
                            <CCol md="3" v-if="scholarship.marks_type.id == 2">
                                <div class="form-group">
                                    <label class="form__label">GPA Scale (for out of)</label>
                                    <input type="number" step="0.01" class="form-control" v-model="scholarship.gpa_scale" />
                                </div>
                            </CCol>
                        </CRow>
                        <CRow>
                            <CCol md="6">
                                <div class="form-group">
                                    <label class="form__label">Currency</label>
                                    <v-select :options="currencyOption" v-model="scholarship.country_id" />
                                </div>
                            </CCol>

                            <CCol md="6">
                                <div class="form-group">
                                    <label class="form__label">Family Income(Annual)</label>
                                    <input type="number" class="form-control" v-model="scholarship.family_income" />
                                </div>
                            </CCol>
                        </CRow>

                        <CRow>
                            <CCol md="4">
                                <div class="form-group">
                                    <label class="form__label">Minimum Age Limit</label>
                                    <input type="number" class="form-control" v-model="scholarship.min_age_limit" />
                                </div>
                            </CCol>
                            <CCol md="4">
                                 <div class="form-group">
                                    <label class="form__label">Maximum Age Limit</label>
                                    <input type="number" class="form-control" v-model="scholarship.max_age_limit" />
                                </div>
                            </CCol>

                            <CCol md="4">
                                <div class="form-group">
                                    <label class="form__label">Gender Specific</label>
                                    <v-select :options="genderOption" v-model="scholarship.gender_specific" />
                                </div>
                            </CCol>
                        </CRow>
                        <CRow>
                            <CCol md="12">
                                <div class="form-group">
                                    <label class="form__label">Other Criteria</label>
                                    <textarea class="form-control" v-model="scholarship.other_criteria" rows="3"></textarea>
                                </div>
                            </CCol>
                        </CRow>
                        <hr />
                        <h5>Additional Information</h5>
                        <CRow>
                            <CCol md="12">
                                <table class="table table-bordered">
                                    <tr>
                                        <th>Sequence</th>
                                        <th>Country</th>
                                        <th>State</th>
                                        <th>Action</th>
                                    </tr>
                                    <tr v-for="(lists,index) in scholarship.countryMenuList">
                                        <td>
                                            {{ index + 1 }}
                                        </td>
                                        <td>
                                            <v-select :id="lists.id" v-model="lists.selected"
                                            @input="onCountryChange(index,lists.selected)"
                                            :options="countryOption"></v-select>
                                        </td>
                                        <td>
                                            <v-select :id="lists.id" v-model="lists.state" :options="lists.stateList"
                                            multiple></v-select>
                                        </td>
                                        <td>
                                            <button type="button" class="btn btn-success" @click="addMenu">
                                            <i class="fa fa-plus"></i>
                                            </button>&nbsp;
                                        </td>
                                    </tr>
                                </table>
                            </CCol>
                        </CRow>
                        <CRow>
                            <CCol md="6">
                                <div class="form-group">
                                    <label class="form__label">Language Requirement</label>
                                    <v-select :options="languageOption" v-model="scholarship.language" multiple />
                                </div>
                            </CCol>
                            <CCol md="6">
                                <div class="form-group">
                                    <label class="form__label">Attachments (PDF, DOC, DOCX, Image)</label>
                                    <input type="file" class="form-control" multiple @change="handleAttachments" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif" />
                                    <ul v-if="scholarship.attachments && scholarship.attachments.length > 0">
                                        <li v-for="(attachment, index) in scholarship.attachments" :key="index">
                                            {{ attachment.name }}
                                        </li>
                                    </ul>
                                </div>
                            </CCol>
                        </CRow>
                        <hr />
                        <h5>Organization Details</h5>
                        <div v-for="(organization, index) in scholarship.organizations" :key="index">
                            <CRow class="mb-2">
                                <CCol md="6">
                                    <div class="form-group">
                                        <label class="form__label">Organization Name</label>
                                        <input type="text" class="form-control" v-model="scholarship.organizations[index].organization_name" />
                                    </div>
                                </CCol>
                                <CCol md="6">
                                    <div class="form-group">
                                        <label class="form__label">University / School Name</label>
                                        <input type="text" class="form-control" v-model="scholarship.organizations[index].university" />
                                    </div>
                                </CCol>
                            </CRow>
                            
                            <CRow>
                                <CCol md="4">
                                    <div class="form-group">
                                        <label class="form__label">Contact Person Name</label>
                                        <input type="text" class="form-control" v-model="scholarship.organizations[index].contact_person_name" />
                                    </div>
                                </CCol>
                                <CCol md="4">
                                    <div class="form-group">
                                        <label class="form__label">Designation</label>
                                        <input type="text" class="form-control" v-model="scholarship.organizations[index].designation" />
                                    </div>
                                </CCol>
                                <CCol md="4">
                                    <div class="form-group">
                                        <label class="form__label">Email</label>
                                        <input type="email" class="form-control" v-model="scholarship.organizations[index].organization_email" />
                                    </div>
                                </CCol>
                            </CRow>

                            <CRow>
                                <CCol md="6">
                                    <div class="form-group">
                                        <label class="form__label">Phone Number</label>
                                        <div class="d-flex"> <div style="width: 30%; margin-right: 10px;"> <v-select :options="countryCodeOption" v-model="scholarship.organizations[index].country_code" />
                                            </div>
                                            <div style="width: 70%;"> <input type="tel" class="form-control" v-model="scholarship.organizations[index].organization_phone" />
                                            </div>
                                        </div>
                                    </div>
                                </CCol>
                                <CCol md="6">
                                    <div class="form-group">
                                        <label class="form__label">Alternate Phone Number</label>
                                        <div class="d-flex"> <div style="width: 30%; margin-right: 10px;"> <v-select :options="altCountryCodeOption" v-model="scholarship.organizations[index].alt_country_code" />
                                            </div>
                                            <div style="width: 70%;"> <input type="tel" class="form-control" v-model="scholarship.organizations[index].organization_alt_phone" />
                                            </div>
                                        </div>
                                    </div>
                                </CCol>
                            </CRow>
                            <CRow>
                                <CCol md="6">
                                    <div class="form-group">
                                        <label class="form__label">Location</label>
                                        <input type="text" class="form-control" v-model="scholarship.organizations[index].location" />
                                    </div>
                                </CCol>

                                <CCol md="6">
                                    <div class="form-group">
                                        <label class="form__label">Website</label>
                                        <input type="url" class="form-control" v-model="scholarship.organizations[index].website" />
                                    </div>
                                </CCol>
                            </CRow>
                        </div>
                    </CCardBody>

                    <CCardFooter>
                        <CButton type="submit" size="sm" color="primary" :disabled="submitted">
                            <CIcon name="cil-check-circle" /> {{ $lang.buttons.general.crud.submit }}
                        </CButton>
                        <CButton class="ml-2" color="danger" size="sm" @click="cancel">
                            <CIcon name="cil-ban" /> {{ $lang.buttons.general.crud.cancel }}
                        </CButton>
                    </CCardFooter>

                </CForm>
            </CCard>
        </CCol>

    </CRow>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import required_span from "../../components/layouts/general/required-span";
import { Mixin } from "/src/mixins/index.js";
import store from "/src/store/store.js";
import { scholarship } from "/src/store/url.js";
import { CCol, CSelect, CButton, CRow, CCard, CCardHeader, CForm, CCardBody, CCardFooter, CAlert, CIcon } from "@coreui/vue";


export default {
    name: "Create",
    components: { required_span, CCol, CSelect, CButton, CRow, CCard, CCardHeader, CForm, CCardBody, CCardFooter, CAlert, CIcon },
    mixins: [Mixin],

    data() {
        return {
            self: this,
            messageColor: "success",
            alertMessage: "",
            showAlert: false,
            dismissCountDown: 0,
            module: scholarship,
            submitted: false,
            applicationModeOption: [
                { id: 1, label: "Online" },
                { id: 2, label: "Offline" },
                { id: 3, label: "Both" },
            ],
            marksTypeOption: [
                { id: 1, label: "Percentage" },
                { id: 2, label: "GPA" }
            ],
            providerTypeOption: [
                { id: 1, label: "Government" },
                { id: 2, label: "Semi-Government" },
                { id: 3, label: "Private" },
            ],
            genderOption: [
                { id: "1", label: "All" },
                { id: "2", label: "Male" },
                { id: "3", label: "Female" }
            ],
            languageOption: [],
            requiredDocumentsOption: [],
            scholarshipTypeOption: [],
            scholarshipTagsOption: [],
            collegeProgrammesOption: [],
            collegeStreamOption: [],
            collegeDisciplinesOption: [],
            countryCodeOption: [],
            altCountryCodeOption: [],
            currencyOption: [],
            countryOption: [],
            stateOption: [],
            scholarship: {
                title: "",
                summary: "",
                academic_year: "",
                application_mode: "",
                application_mode_value: "",
                provider_type: "",
                type: "",
                college_programme: "",
                college_stream: "",
                college_disciplines: "",
                application_url: "",
                how_to_apply: "",
                language: "",
                startDate: "",
                endDate: "",
                result_date: "",
                country: "",
                state: "",
                benefits: "",
                attachments: [],
                required_document: [],
                tags: "",
                min_percentage: null,
                gpa_score: null,
                gpa_scale: null,
                marks_type: {},
                family_income: null,
                country_id: null,
                min_age_limit: null,
                max_age_limit: null,
                gender_specific: "",
                other_criteria: "",
                organizations: [{  organization_name: '', university: '', contact_person_name: '', designation: '', organization_email: '', country_code:'', organization_phone: '', alt_country_code:'',organization_alt_phone: '', location: '', website: '' }],
                countryMenuList: [{
                    id: '0',
                    selected: '',
                    state: [],
                    stateList: []
                }],
                application_locations: [], // new
            },
            menuCounter: 0,
        }
    },

    validations: {
        scholarship: {
            title: { required },
            type: { required },
            summary: { required },
            startDate: { required },
            endDate: { required },
            application_mode: { required }
        },
    },
    mounted() {
        let self = this;
        store.commit('showLoader', false);
        self.dismissCountDown = 0;
        localStorage.setItem("showAlert", "");

        axios.post(self.module.api.document).then((response) => {
            self.requiredDocumentsOption = response.data.data.map(requiredDocumentsData => ({
                id: requiredDocumentsData.id,
                label: requiredDocumentsData.title,
            }));
        }).catch((error) => {
            console.error("Failed to fetch requiredDocumentsData", error);
            this.scholarshipTagsOption = [];
        });

        axios.post(self.module.api.language).then((response) => {
            self.languageOption = response.data.data.map(languageData => ({
                id: languageData.id,
                label: languageData.title,
            }));
        }).catch((error) => {
            console.error("Failed to fetch language", error);
            this.scholarshipTagsOption = [];
        });


        axios.post(self.module.api.collegeProgrammes).then((response) => {
            self.collegeProgrammesOption = response.data.data;
        }).catch((error) => {
            console.error("Failed to fetch scholarshipType list", error);
            this.collegeProgrammesOption = [];
        });

        axios.post(self.module.api.type).then((response) => {
            self.scholarshipTypeOption = response.data.data;
        }).catch((error) => {
            console.error("Failed to fetch scholarshipType list", error);
            this.scholarshipTypeOption = [];
        });

        axios.get(self.module.api.currency).then((response) => {
            self.currencyOption = response.data.data.map(currencyData => ({
                id: currencyData.id,
                label: `${currencyData.currencySymbol} (${currencyData.name})`,
            }));
            self.countryOption = response.data.data.map(currencyData => ({
                id: currencyData.id,
                label: currencyData.name,
            }));
            self.countryCodeOption = response.data.data.map(currencyData => {
                const dialCode = String(currencyData.dialCode); // Ensure it's a string
                const formattedDialCode = dialCode.startsWith('+') ? dialCode : '+' + dialCode;
                return {
                    id: currencyData.id,
                    label: formattedDialCode,
                };
            });

            self.altCountryCodeOption = response.data.data.map(currencyData => {
                const dialCode = String(currencyData.dialCode); // Ensure it's a string
                const formattedDialCode = dialCode.startsWith('+') ? dialCode : '+' + dialCode;
                return {
                    id: currencyData.id,
                    label: formattedDialCode,
                };
            });
        }).catch((error) => {
            console.error("Failed to fetch currency and country list", error);
            this.currencyOption = [];
            this.countryOption = [];
            this.countryCodeOption = [];
            this.altCountryCodeOption = [];
        });

        axios.post(self.module.api.tags,{'entity' : "Scholarship"}).then((response) => {
            self.scholarshipTagsOption = response.data.data.map(tagsData => ({
                id: tagsData.id,
                label: tagsData.title,
            }));
        }).catch((error) => {
            console.error("Failed to fetch currency and country list", error);
            this.scholarshipTagsOption = [];
        });
    },
    methods: {
        cancel() {
            this.$router.push({ path: "/scholarship" });
        },
        onCollegeProgrammesChange(collegeProgrammes) {
            console.log(collegeProgrammes);
            let self = this;
            const collegeProgrammeId = collegeProgrammes.id;
            if (collegeProgrammeId) {
                axios.post(self.module.api.collegeStream, { programmeId: collegeProgrammeId }).then(response => {
                    self.collegeStreamOption = response.data.data.map(collegeStreamData => ({
                        id: collegeStreamData.id,
                        label: collegeStreamData.title,
                    }));
                });
            } else {
                self.collegeStreamOption = [];
            }
        },
        onCollegeStreamChange(collegeStream) {
            let self = this;
            const collegeStreamId = collegeStream.id;
            if (collegeStreamId) {
                axios.post(self.module.api.collegeDiscipline, { streamId: collegeStreamId }).then(response => {
                    self.collegeDisciplinesOption = response.data.data.map(collegeDisciplinesData => ({
                        id: collegeDisciplinesData.id,
                        label: collegeDisciplinesData.title,
                    }));
                });
            } else {
                self.collegeDisciplinesOption = [];
            }
        },
        onCountryChange(index, val) {

            // console.log();
            let self = this;
            var postData = {
                'countryId' : val.id
            };
            axios.post(self.module.api.states, postData).then((response) => {
                self.scholarship.countryMenuList[index].state = [];
                self.scholarship.countryMenuList[index].stateList = [];
                response.data.data.map(function (value, key) {
                    //console.log({value: value.id, label: value.name});
                    self.scholarship.countryMenuList[index].stateList.push({id: value.id, label: value.name});
                });
            });
        },
        addMenu() {
            this.scholarship.countryMenuList.push({
                id: `${++this.menuCounter}`,
                selected: '',
                state: [],
                stateList: [],
            });
        },
        onCountryChangeOld(country) {
            let self = this;
            const countryId = country.id;
            if (countryId) {
                axios.post(self.module.api.states, { countryId: countryId }).then(response => {
                    self.stateOption = response.data.data.map(stateData => ({
                        id: stateData.id,
                        label: stateData.name,
                    }));
                });
            } else {
                self.stateOption = [];
            }
        },
        onApplicationModeChange(mode) {
            // console.log("mode",mode);
            if (mode.id === 2 || mode.id === 3) { // Offline
                if (!this.scholarship.application_locations || this.scholarship.application_locations.length === 0) {
                    this.scholarship.application_locations = [""]; // Initialize with one empty location
                }
            } else {
                this.scholarship.application_locations = []; // Clear locations
            }
            this.scholarship.application_mode_value = mode.id;
        },
        onMarksTypeChange(type) {
            // Save selected marks_type ID

            // Clear values when switching
            if (type.id === 1) {
                this.scholarship.gpa_score = null;
            } else if (type.id === 2) {
                this.scholarship.min_percentage = null;
            }
        },
        addLocation() {
            this.scholarship.application_locations.push("");
        },
        removeLocation(index) {
            this.scholarship.application_locations.splice(index, 1);
        },
        onSubmit() {
            this.$v.$touch();
            if (!this.$v.$invalid) {
                
                const formData = new FormData();
                for (let key in this.scholarship) {
                    if (key === 'attachments') {
                        
                        this.scholarship.attachments.forEach((file, index) => {
                            formData.append(`attachments[${index}]`, file); // Append each file with a unique key
                        });
                    } else if (key === 'organizations') {
                         this.scholarship.organizations.forEach((org, index) => {
                            for(let orgKey in org){
                                if (orgKey === 'country_code' || orgKey === 'alt_country_code') {
                                    // Ensure the value is an object before attempting to iterate
                                    if (typeof org[orgKey] === 'object' && org[orgKey] !== null) {
                                        // Append id and label of the country code object
                                        formData.append(`organizations[${index}][${orgKey}][id]`, org[orgKey].id || org[orgKey].value);
                                        formData.append(`organizations[${index}][${orgKey}][label]`, org[orgKey].label);
                                    }
                                } else {
                                    formData.append(`organizations[${index}][${orgKey}]`, org[orgKey]);
                                }
                            }
                         })
                    } else if (key === 'countryMenuList') { // Handle countryMenuList
                        this.scholarship.countryMenuList.forEach((countryMenu, countryIndex) => {
                            formData.append(`countryMenuList[${countryIndex}][id]`, countryMenu.id);
                            if(countryMenu.selected){
                                for (let selectedKey in countryMenu.selected) {
                                    formData.append(`countryMenuList[${countryIndex}][selected][${selectedKey}]`, countryMenu.selected[selectedKey]);
                                }
                            }


                            countryMenu.state.forEach((state, stateIndex) => {
                                 if(typeof state === 'object' && state !== null){
                                    for(let stateKey in state){
                                         formData.append(`countryMenuList[${countryIndex}][state][${stateIndex}][${stateKey}]`, state[stateKey]);
                                    }
                                 }
                                 else{
                                     formData.append(`countryMenuList[${countryIndex}][state][${stateIndex}]`, state);
                                 }
                            });
                           
                        });
                    }
                    else if (Array.isArray(this.scholarship[key])) {
                        this.scholarship[key].forEach((item, index) => {
                            if (typeof item === 'object' && item !== null) {
                                for (let itemKey in item) {
                                     formData.append(`${key}[${index}][${itemKey}]`, item[itemKey]);
                                }

                            }
                            else{
                                 formData.append(`${key}[${index}]`, item);
                            }
                        });
                    }
                     else if (typeof this.scholarship[key] === 'object' && this.scholarship[key] !== null) {
                        // Handle nested object,  append each property of the object
                        for (let propKey in this.scholarship[key]) {
                            formData.append(key + '[' + propKey + ']', this.scholarship[key][propKey]);
                        }
                    }
                    else {
                        formData.append(key, this.scholarship[key]);
                    }
                }
                this.submitted = true;
                setTimeout(() => {
                    axios.post(scholarship.api.create, formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    }).then((response) => {
                        this.submitted = false;
                        if (response.data.code == 200) {
                            this.alertMessage = response.data.message;
                            this.messageColor = "success";
                            this.showAlert = true;
                            this.dismissCountDown = 10;
                            this.$router.push({name: "Scholarship List"});
                        } else {
                            this.messageColor = "danger";
                            this.alertMessage = response.data.message || (response.data.error ? response.data.error[0] : "Something went wrong.");
                            this.showAlert = true;
                            this.dismissCountDown = 10;
                        }
                    })
                    .catch((error) => {
                        this.submitted = false;
                        this.messageColor = "danger";
                        if (error.response && error.response.data) {
                            this.alertMessage = error.response.data.message || (error.response.data.error ? error.response.data.error[0] : "Validation error.");
                        } else {
                            this.alertMessage = "Something went wrong.";
                        }
                        this.showAlert = true;
                        this.dismissCountDown = 10;
                    });
                }, 1000);
            } else {
                this.alertMessage = "Please fill in all required fields before submitting.";
                this.messageColor = "danger";
                this.showAlert = true;
                this.dismissCountDown = 5;
            }
        },
        handleAttachments(event) {
            this.scholarship.attachments = Array.from(event.target.files);
        }
    }
};
</script>

<style scoped>
.error {
    color: red;
}
.margin-bottom-20 {
    margin-bottom: 20px;
}
</style>